import {
  createItem,
  deleteItem,
  getItem,
  listItems,
  updateItem,
  uploadLibraryFile,
} from './sharepoint';
import {
  MILEAGE_RATE_USD_PER_MILE,
  calcMileageDollars,
} from '../config/rates';

const LIST = 'timesheets';

/**
 * Timesheets service — one row per (submitter, week) in SharePoint, with
 * the grid body stored as a JSON blob in RowsJson.
 *
 * Lifecycle: Draft → Pending Approval → Approved, with Rejected as a side
 * channel the RM can use to bounce a submission back with a note. The
 * submitter can edit while Draft or Rejected; the RM is the only one who
 * can edit an Approved row; a Pending Approval row is read-only for both
 * sides until the RM either approves or rejects it.
 *
 * The rollup columns (TotalHours, TotalMiles, MileageRateUsed,
 * TotalMileageDollars) are recomputed from RowsJson on every save so the
 * Billing dashboard can sort/filter without having to parse JSON.
 */

export const COVERAGE_TYPES = [
  'Standard',
  'Overage',
  'Expenses',
  'Mileage',
];

// Display labels for the coverage types — stored values stay short
// and code-friendly ('Standard' / 'Overage'), while the UI surfaces a
// more descriptive label. This keeps existing rows in SharePoint
// readable without a data migration: anywhere we render a coverage
// type, we look the stored value up in this map and fall back to the
// raw value for forward-compat (a new type added later still shows up
// in lists even before we ship a label for it). 'Other' is retained
// in the label map (not in COVERAGE_TYPES) so legacy rows that were
// saved as 'Other' before we dropped the option still render their
// stored category instead of falling back to a raw value.
export const COVERAGE_TYPE_LABELS = {
  Standard: 'Standard Liaison Hours',
  // Renamed from "Extra Liaison Hours" (per Jillian, July 2026) — these
  // hours bill the supplier at OT_RATE_MULTIPLIER × the standard rate.
  Overage: 'OT Liaison Hours',
  Expenses: 'Expenses',
  Mileage: 'Mileage',
  Other: 'Other',
};

// Subset that unlocks the Explanation column in the grid. Standard rows
// are the common case and shouldn't be gated behind an explanation.
export const EXPLANATION_REQUIRED_COVERAGE_TYPES = new Set([
  'Overage',
  'Expenses',
  'Mileage',
]);

// Ordered list of per-day field keys. Used by the grid (column order) and
// by the rollup math (sum across days).
// The billing week runs Sunday → Saturday, so the day columns are
// ordered Sunday-first. This order must stay in lock-step with
// weekStartFromDate (which returns the Sunday) and weekDates (which
// walks 7 days from that Sunday) — index i here maps to weekDates[i].
export const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// Status lifecycle constants. Exported so pages / components can compare
// against the same string values the service reads.
export const STATUS = {
  DRAFT: 'Draft',
  PENDING: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

// States in which the submitter may edit the grid.
export const SUBMITTER_EDITABLE_STATUSES = new Set([
  STATUS.DRAFT,
  STATUS.REJECTED,
]);

// ---- Adjustments (supplements + credits) ----------------------------
//
// An "adjustment" is a follow-up timesheet record attached to an already-
// approved (and possibly already-invoiced) timesheet. Two flavors:
//
//   • supplement — captures missed entries the submitter forgot. Hours
//     and dollars push the period total UP. Approved supplements flow
//     into the normal billing aggregation alongside the original.
//   • credit — captures over-billed dollars that need to come back off
//     the period total. Stored as a flat dollar amount (CreditAmount)
//     rather than row hours, because credits are typically issued in
//     dollar terms regardless of which row was over-logged.
//
// Both flavors land in Status='Pending' and flow through the normal RM
// approval queue. Both reference the parent via OriginalTimesheetId,
// both carry an AdjustmentReason (free-form explanation), and both
// have a BillingDisposition picked by the creator that tells the
// invoice generator how to handle the dollar impact on the customer
// invoice. Disposition values differ by adjustment type — supplements
// add money to a customer's bill (new invoice or PPA line), while
// credits subtract (credit memo or credit line on next invoice).
export const ADJUSTMENT_TYPES = {
  SUPPLEMENT: 'supplement',
  CREDIT: 'credit',
};

// BillingDisposition values — all four live in a single map so the form
// can render the right two radios per adjustment type and the invoice
// generator (next project) can switch on the value without re-deriving
// the type. Supplement dispositions ADD to the customer's outstanding
// balance; credit dispositions SUBTRACT.
export const BILLING_DISPOSITIONS = {
  SUPPLEMENTAL_INVOICE: 'supplemental-invoice',
  NEXT_INVOICE: 'next-invoice',
  CREDIT_MEMO: 'credit-memo',
  NEXT_INVOICE_CREDIT: 'next-invoice-credit',
};
export const BILLING_DISPOSITION_LABELS = {
  [BILLING_DISPOSITIONS.SUPPLEMENTAL_INVOICE]: 'Issue Supplemental Invoice',
  [BILLING_DISPOSITIONS.NEXT_INVOICE]:
    'Add as Prior Period Adjustment on Next Invoice',
  [BILLING_DISPOSITIONS.CREDIT_MEMO]: 'Issue Credit Memo',
  [BILLING_DISPOSITIONS.NEXT_INVOICE_CREDIT]:
    'Add as Credit Line on Next Invoice',
};

// Convenience predicates so the UI can ask
// "which dispositions belong with this adjustment type?" without
// hard-coding the matrix at the call site.
export const SUPPLEMENT_DISPOSITIONS = [
  BILLING_DISPOSITIONS.SUPPLEMENTAL_INVOICE,
  BILLING_DISPOSITIONS.NEXT_INVOICE,
];
export const CREDIT_DISPOSITIONS = [
  BILLING_DISPOSITIONS.CREDIT_MEMO,
  BILLING_DISPOSITIONS.NEXT_INVOICE_CREDIT,
];

// ---- Week math ------------------------------------------------------

/**
 * Given any Date / date string / null, return the Monday of that week as
 * a YYYY-MM-DD string. Sunday is treated as part of the PRIOR week (so the
 * week runs Mon → Sun on the grid) — this matches the screenshot mockup
 * and Proxemics's payroll cadence.
 *
 * Works off local-date parts (no UTC shift) so "2026-04-23" doesn't roll
 * back a day in timezones west of UTC.
 */
export function weekStartFromDate(input) {
  const d = input ? new Date(input) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  // 0=Sun, 1=Mon, ... 6=Sat. The billing week starts on SUNDAY, so
  // offset back to the most-recent Sunday (subtract the weekday index).
  const day = d.getDay();
  const sun = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
  return formatLocalDate(sun);
}

// Return an array of 7 local-date Date objects, Sunday through Saturday,
// for a given weekStart (YYYY-MM-DD Sunday string).
export function weekDates(weekStart) {
  const base = parseLocalDate(weekStart);
  if (!base) return [];
  return Array.from({ length: 7 }, (_, i) => {
    return new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
  });
}

// Jump a weekStart string forward or backward by N weeks.
export function shiftWeek(weekStart, deltaWeeks) {
  const base = parseLocalDate(weekStart);
  if (!base) return weekStart;
  const shifted = new Date(
    base.getFullYear(),
    base.getMonth(),
    base.getDate() + deltaWeeks * 7,
  );
  return formatLocalDate(shifted);
}

// Parse YYYY-MM-DD without any timezone drift. Returns null on invalid input.
function parseLocalDate(value) {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// Inverse of parseLocalDate.
function formatLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// ---- Row + totals -----------------------------------------------------

/**
 * Shape of a single grid row. Exported so the UI can mint blank rows
 * without knowing the internal shape, and kept as a plain factory
 * (vs. a class) so JSON round-tripping stays trivial.
 */
export function emptyRow(coverageType = 'Standard') {
  return {
    plantCode: '',
    supplierCode: '',
    // Which of the supplier's service rows at this plant these hours
    // belong to (e.g. 'Standard Coverage' vs 'Lineside Support'). Blank
    // on legacy rows → the plant's only / default row. See
    // findAssignment() in services/suppliers.js.
    service: '',
    secondaryFacility: '',
    coverageType,
    // Row-level note. Still used by OT / Mileage / Expenses rows and by
    // weekly-billing Standard rows (which are judged on the week's total).
    // Standard rows judged day by day now carry one note PER flagged day
    // in `dayExplanations` (Sep 2026); `explanation` stays as the legacy
    // single note so trackers submitted before then read unchanged.
    explanation: '',
    // { mon: 'note', tue: 'note', ... } — only days that were off
    // standard when the tracker was saved. Empty on other row types.
    dayExplanations: {},
    // Per-day values. For Standard/Overage/Other these are hours; for
    // Mileage they're miles. Expenses rows ignore this grid entirely
    // and use expenseAmount (below) instead, since an expense is a
    // single dollar figure, not a per-day metric.
    values: { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 },
    // Dollar amount for Expenses rows. Zero / unused on every other
    // coverage type.
    expenseAmount: 0,
    // Full ISO date ('YYYY-MM-DD') an Expenses row is from — the Day
    // dropdown lets the submitter pick any day of the current month.
    // Optional; unused on other coverage types (per Jillian, Aug 2026).
    // expenseDay is the legacy day-key form kept only for back-compat.
    expenseDate: '',
    expenseDay: '',
    // Full ISO date a Mileage row is from — the Day dropdown offers any
    // day of the current month. The miles themselves live in the value
    // grid on the weekday the date falls on, so the billing engine (which
    // reads the grid) keeps working; mileageDate rides along for display.
    // mileageDay is the legacy day-key form kept only for back-compat.
    mileageDate: '',
    mileageDay: '',
    // Who to pay — display name of the team member this Expense or
    // Mileage row reimburses (per Jillian, July 2026). Picked from the
    // team members assigned to the row's plant; required at submit for
    // Expenses / Mileage rows. Blank / unused on hour-type rows.
    assignedTo: '',
    // Receipt attachment for Expenses rows. Populated by
    // uploadTimesheetReceipt() — receiptFileName is the human-readable
    // canonical name shown in the UI; receiptUrl is the SharePoint
    // preview link the filename click-target opens. Both blank on
    // every non-Expenses row.
    receiptFileName: '',
    receiptUrl: '',
    // Written overage-approval attachment. Populated by
    // uploadTimesheetOverageApproval() and anchored on the (plant,
    // supplier) Standard row when the supplier requires written approval
    // for going over standard hours. One file per (supplier, plant, week).
    overageApprovalFileName: '',
    overageApprovalUrl: '',
  };
}

/**
 * Build the canonical filename for an expense receipt. Pattern is
 * "TeamMember-Plant-Supplier-ExpenseReceipt-YYYY-MM-DD.ext", with each
 * segment slug-cased so the resulting name survives SharePoint's
 * filename rules (no spaces, no slashes, no exotic punctuation). Falls
 * back to "Unknown" for any segment we don't have a value for so a
 * partial upload doesn't blow up — finance can still reconcile the row
 * by clicking through, and the canonical pieces show up in the
 * SharePoint folder for filterable native views.
 */
export function buildReceiptFileName({
  teamMemberName,
  plantCode,
  supplierCode,
  weekStart,
  originalName,
}) {
  const slug = (s) =>
    String(s || 'Unknown')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'Unknown';
  const ext = (() => {
    const m = String(originalName || '').match(/\.([A-Za-z0-9]+)$/);
    return m ? `.${m[1].toLowerCase()}` : '';
  })();
  // weekStart is the Monday-anchored ISO date for the timesheet week —
  // used here so multiple expense rows for the same week land in the
  // library with predictable, sortable filenames.
  const date = String(weekStart || '').slice(0, 10) || 'NoDate';
  return [
    slug(teamMemberName),
    slug(plantCode),
    slug(supplierCode),
    'ExpenseReceipt',
    date,
  ].join('-') + ext;
}

/**
 * Upload an expense receipt to the TimesheetReceipts SharePoint document
 * library. The file is renamed to buildReceiptFileName() before upload
 * so the library is browsable from SharePoint without opening each
 * receipt to figure out what it belongs to.
 *
 * Returns `{ fileName, webUrl, id }` so the caller can stash both
 * onto the row's receiptFileName / receiptUrl fields. SharePoint's
 * `conflictBehavior: rename` keeps re-uploads from clobbering an
 * earlier receipt; the library returns the actual stored name (which
 * may have a counter suffix on collision) so the displayed filename
 * matches what's on disk.
 */
export async function uploadTimesheetReceipt({
  file,
  teamMemberName,
  plantCode,
  supplierCode,
  weekStart,
  postedBy = '',
}) {
  if (!file) {
    throw new Error('uploadTimesheetReceipt: a file is required.');
  }
  const targetName = buildReceiptFileName({
    teamMemberName,
    plantCode,
    supplierCode,
    weekStart,
    originalName: file.name,
  });
  // Browser File objects expose a constructor that accepts the original
  // bytes plus a new name — no need to read/copy. Type defaults to the
  // original so SharePoint's preview behavior (PDF preview, image
  // preview) keeps working.
  const renamed =
    typeof File !== 'undefined' && file instanceof File
      ? new File([file], targetName, { type: file.type })
      : file;
  const result = await uploadLibraryFile('timesheetReceipts', renamed, {
    PostedBy: postedBy,
    PostedAt: new Date().toISOString(),
  });
  return {
    id: result.id,
    fileName: result.fileName || targetName,
    webUrl: result.webUrl || '',
  };
}

/**
 * Canonical filename for a written overage-approval file. Same shape as
 * the receipt name but with an "OverageApproval" tag so the two kinds of
 * file are easy to tell apart in the shared TimesheetReceipts library:
 * "TeamMember-Plant-Supplier-OverageApproval-YYYY-MM-DD.ext".
 */
export function buildOverageApprovalFileName({
  teamMemberName,
  plantCode,
  supplierCode,
  weekStart,
  originalName,
}) {
  const slug = (s) =>
    String(s || 'Unknown')
      .replace(/[^A-Za-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'Unknown';
  const ext = (() => {
    const m = String(originalName || '').match(/\.([A-Za-z0-9]+)$/);
    return m ? `.${m[1].toLowerCase()}` : '';
  })();
  const date = String(weekStart || '').slice(0, 10) || 'NoDate';
  return (
    [
      slug(teamMemberName),
      slug(plantCode),
      slug(supplierCode),
      'OverageApproval',
      date,
    ].join('-') + ext
  );
}

/**
 * Upload a written overage-approval file. Some suppliers require written
 * approval before a liaison bills hours over the standard allocation;
 * this stores that document in the same TimesheetReceipts library (with
 * a distinct filename), and the caller stashes the returned fileName /
 * webUrl onto the (plant, supplier) row's overageApprovalFileName /
 * overageApprovalUrl fields. One file per (supplier, plant, week).
 */
export async function uploadTimesheetOverageApproval({
  file,
  teamMemberName,
  plantCode,
  supplierCode,
  weekStart,
  postedBy = '',
}) {
  if (!file) {
    throw new Error('uploadTimesheetOverageApproval: a file is required.');
  }
  const targetName = buildOverageApprovalFileName({
    teamMemberName,
    plantCode,
    supplierCode,
    weekStart,
    originalName: file.name,
  });
  const renamed =
    typeof File !== 'undefined' && file instanceof File
      ? new File([file], targetName, { type: file.type })
      : file;
  const result = await uploadLibraryFile('timesheetReceipts', renamed, {
    PostedBy: postedBy,
    PostedAt: new Date().toISOString(),
  });
  return {
    id: result.id,
    fileName: result.fileName || targetName,
    webUrl: result.webUrl || '',
  };
}

// Return the row's headline number:
//   Standard / Overage / Other  → sum of day hours
//   Mileage                     → sum of day miles
//   Expenses                    → the single expenseAmount (dollars)
//
// 0.25-step hours stay exact (7.75 round-trips cleanly); we round to 2
// decimals on the way out to guard against floating-point drift.
/**
 * Does this row carry anything the submitter actually entered — hours,
 * miles, an expense amount, an explanation, a Paid-To, or a chosen day?
 * A row with only Plant + Supplier is an auto-seeded template nobody
 * touched (the tracker pre-seeds every assigned plant × supplier), so it
 * is NOT data. Mirrors the validation rule in Timesheets.jsx.
 */
export function rowHasData(row) {
  if (!row) return false;
  if (row.explanation && String(row.explanation).trim()) return true;
  if (
    row.dayExplanations &&
    Object.values(row.dayExplanations).some((t) => String(t || '').trim())
  ) {
    return true;
  }
  if (String(row.assignedTo || '').trim()) return true;
  if ((Number(row.expenseAmount) || 0) > 0) return true;
  if (row.mileageDate || row.mileageDay) return true;
  const values = row.values || {};
  return DAY_KEYS.some((k) => (Number(values[k]) || 0) > 0);
}

/**
 * Drop untouched / zero-hour rows. Applied when a tracker is submitted
 * (and when an RM saves edits to a pending one) so a supplier with no
 * hours that week never reaches the Approvals page, billing, or an
 * invoice (per Jillian, Sep 2026). Drafts keep every row so the
 * submitter's template stays intact week to week.
 */
export function stripEmptyRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter(rowHasData);
}

// Short weekday labels used on per-day explanation boxes and in the
// Approvals / invoice "Details" text: "SU 09/20", "M 09/21", ...
const DAY_ABBREV_BY_JS_DAY = ['SU', 'M', 'TU', 'W', 'TH', 'F', 'SA'];

/** "SU 09/20" for a Date. Falls back to the day key when no date. */
export function dayLabel(date, dayKey) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return String(dayKey || '').toUpperCase();
  }
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${DAY_ABBREV_BY_JS_DAY[date.getDay()]} ${mm}/${dd}`;
}

/**
 * The explanation lines to show for a row, oldest day first:
 *   [{ key: 'sun', label: 'SU 09/20', text: '...' }, ...]
 * Per-day notes win when the row has any; otherwise the legacy single
 * `explanation` comes back as one unlabeled line. Empty array when the
 * row has no notes at all. `weekStart` (YYYY-MM-DD or Date) lets the
 * labels carry real dates; without it they fall back to day names.
 */
export function explanationLines(row, weekStart) {
  const per = row?.dayExplanations;
  const entries = per && typeof per === 'object'
    ? DAY_KEYS
        .map((k) => [k, String(per[k] || '').trim()])
        .filter(([, text]) => text)
    : [];
  if (entries.length) {
    const dates = weekStart ? weekDates(weekStart) : [];
    return entries.map(([k, text]) => {
      const idx = DAY_KEYS.indexOf(k);
      return { key: k, label: dayLabel(dates[idx], k), text };
    });
  }
  const single = String(row?.explanation || '').trim();
  return single ? [{ key: '', label: '', text: single }] : [];
}

/**
 * Plain-text version of explanationLines() for invoice line details,
 * PDFs and other single-string readers: "SU 09/20: ...; M 09/21: ...".
 * A legacy single note comes back verbatim.
 */
export function explanationText(row, weekStart) {
  return explanationLines(row, weekStart)
    .map((l) => (l.label ? `${l.label}: ${l.text}` : l.text))
    .join('; ');
}

export function rowTotal(row) {
  if (!row) return 0;
  if ((row.coverageType || 'Standard') === 'Expenses') {
    return Math.round((Number(row.expenseAmount) || 0) * 100) / 100;
  }
  if (!row.values) return 0;
  const sum = DAY_KEYS.reduce((acc, k) => acc + (Number(row.values[k]) || 0), 0);
  return Math.round(sum * 100) / 100;
}

/**
 * Standard work-week threshold beyond which any (plant, supplier) pair
 * counts as overtime. Centralized here so the submitter view, the RM
 * approval view, and any future report can stay in agreement.
 */
export const OT_THRESHOLD_HOURS = 40;

/**
 * Multiplier applied to the hourly rate for hours over the threshold,
 * but only for (plant, supplier) pairs the RM has explicitly checked
 * "Bill X Hours at OT Rate" on. Pairs without OT approval bill every
 * hour at the regular rate, even if they're over 40.
 */
export const OT_RATE_MULTIPLIER = 1.5;

/**
 * Detect overtime per (plant, supplier) pair on a single week.
 *
 * Walks the rows once, summing hour-type entries (Standard, Overage,
 * Other) per pair. Mileage rows store miles in the day cells, not
 * hours, so they're excluded; Expenses rows have no day grid at all.
 *
 * Returns:
 *   pairs   — Map<"PLANT|SUPPLIER" → { plantCode, supplierCode, hours,
 *             ot }> covering every pair found in the week
 *   overtime — array of pairs whose hours exceed OT_THRESHOLD_HOURS,
 *              sorted highest-hours-first for display
 *   keysOver — Set of "PLANT|SUPPLIER" keys for the over-40 pairs, so
 *              the UI can flag a row in O(1) without re-scanning
 */
export function computeOvertimeByPair(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const pairs = new Map();
  for (const r of list) {
    const type = r?.coverageType || 'Standard';
    if (type === 'Mileage' || type === 'Expenses') continue;
    const plantCode = String(r?.plantCode || '').trim().toUpperCase();
    const supplierCode = String(r?.supplierCode || '').trim().toUpperCase();
    if (!plantCode || !supplierCode) continue;
    const key = `${plantCode}|${supplierCode}`;
    const sum = DAY_KEYS.reduce(
      (acc, k) => acc + (Number(r?.values?.[k]) || 0),
      0,
    );
    const prior = pairs.get(key);
    if (prior) {
      prior.hours = Math.round((prior.hours + sum) * 100) / 100;
    } else {
      pairs.set(key, {
        plantCode,
        supplierCode,
        hours: Math.round(sum * 100) / 100,
      });
    }
  }
  // Tag each pair with its OT amount (positive only) and pull the
  // ones that breach the threshold into a sorted array.
  for (const p of pairs.values()) {
    p.ot =
      p.hours > OT_THRESHOLD_HOURS
        ? Math.round((p.hours - OT_THRESHOLD_HOURS) * 100) / 100
        : 0;
  }
  const overtime = Array.from(pairs.values())
    .filter((p) => p.ot > 0)
    .sort((a, b) => b.hours - a.hours);
  const keysOver = new Set(
    overtime.map((p) => `${p.plantCode}|${p.supplierCode}`),
  );
  return { pairs, overtime, keysOver };
}

/**
 * Compute the four rollup numbers stored on the SharePoint row. Split
 * out so both the save path and any future read-time recompute can share
 * the logic. `rate` defaults to the current IRS rate constant but
 * callers can override when re-approving a historical timesheet.
 */
export function computeTotals(rows, rate = MILEAGE_RATE_USD_PER_MILE) {
  const list = Array.isArray(rows) ? rows : [];
  let totalHours = 0;
  // Commission hours: OT (Overage) counts at 1.5×, Standard / Other 1:1.
  // Billed hours (totalHours) stay 1:1 — the OT premium rides on the rate
  // at billing time (per Jillian, Aug/Sep 2026).
  let totalCommissionHours = 0;
  let totalMiles = 0;
  let totalExpenseDollars = 0;
  for (const r of list) {
    const t = rowTotal(r);
    const type = r.coverageType || 'Standard';
    if (type === 'Mileage') {
      totalMiles += t;
    } else if (type === 'Expenses') {
      totalExpenseDollars += t;
    } else {
      totalHours += t;
      totalCommissionHours += type === 'Overage' ? t * OT_RATE_MULTIPLIER : t;
    }
  }
  totalHours = Math.round(totalHours * 100) / 100;
  totalCommissionHours = Math.round(totalCommissionHours * 100) / 100;
  totalMiles = Math.round(totalMiles * 100) / 100;
  totalExpenseDollars = Math.round(totalExpenseDollars * 100) / 100;
  const totalMileageDollars = calcMileageDollars(totalMiles);
  return {
    totalHours,
    totalCommissionHours,
    totalMiles,
    totalMileageDollars,
    totalExpenseDollars,
    mileageRateUsed: rate,
  };
}

// ---- Encode / decode --------------------------------------------------

function decode(row) {
  if (!row) return row;
  let rows = [];
  if (row.RowsJson) {
    try {
      rows = JSON.parse(row.RowsJson);
      if (!Array.isArray(rows)) rows = [];
    } catch {
      rows = [];
    }
  }
  // OvertimeApprovalsJson — list of "PLANTCODE|SUPPLIERCODE" pair
  // keys the RM checked the "Bill X Hours at OT Rate" box on. Empty
  // / missing means no OT was approved on the timesheet, so billing
  // applies the regular rate to every hour even if a pair went over
  // 40. Stored as a JSON array; surfaced to callers as a deduped
  // string array of upper-cased pair keys.
  let overtimeApprovals = [];
  if (row.OvertimeApprovalsJson) {
    try {
      const raw = JSON.parse(row.OvertimeApprovalsJson);
      if (Array.isArray(raw)) {
        const seen = new Set();
        for (const entry of raw) {
          const key = String(entry || '').trim().toUpperCase();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          overtimeApprovals.push(key);
        }
      }
    } catch {
      overtimeApprovals = [];
    }
  }
  // Adjustment metadata — covers both supplements (extra hours/dollars
  // we forgot to bill) and credits (dollars we over-billed). Normalized
  // here so the rest of the app can rely on simple booleans without
  // re-deriving the matrix at each call site.
  //
  // Source-of-truth column going forward is AdjustmentType ('supplement'
  // | 'credit' | ''). For backward compat we also accept the legacy
  // IsSupplement Yes/No column written by earlier saves — if the new
  // column is missing but IsSupplement is true, we treat the row as a
  // supplement. SupplementReason is the legacy name for what we now
  // call AdjustmentReason; we read both and surface whichever has
  // content.
  // Per-plant weekly notes — a plain object keyed by plant code
  // ({ FZA: 'note…' }). Stored as JSON in the PlantNotesJson column so
  // the notes stay completely out of RowsJson and can never leak into
  // billing / invoice row math (per Jillian, Aug 2026).
  let plantNotes = {};
  if (row.PlantNotesJson) {
    try {
      const parsed = JSON.parse(row.PlantNotesJson);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        plantNotes = parsed;
      }
    } catch {
      plantNotes = {};
    }
  }
  const adjustmentTypeRaw = String(row.AdjustmentType || '').toLowerCase();
  const knownAdjustmentTypes = new Set(Object.values(ADJUSTMENT_TYPES));
  const isSupplementLegacy =
    !adjustmentTypeRaw &&
    (row.IsSupplement === true ||
      row.IsSupplement === 1 ||
      String(row.IsSupplement || '').toLowerCase() === 'true' ||
      String(row.IsSupplement || '').toLowerCase() === 'yes');
  const adjustmentType = knownAdjustmentTypes.has(adjustmentTypeRaw)
    ? adjustmentTypeRaw
    : isSupplementLegacy
      ? ADJUSTMENT_TYPES.SUPPLEMENT
      : '';
  const isSupplement = adjustmentType === ADJUSTMENT_TYPES.SUPPLEMENT;
  const isCredit = adjustmentType === ADJUSTMENT_TYPES.CREDIT;
  const isAdjustment = Boolean(adjustmentType);
  const creditAmountNum = Number(row.CreditAmount);
  const supplementAmountNum = Number(row.SupplementAmount);
  // Hours-based adjustments (per Jillian, July 2026): when > 0 the
  // adjustment is billed at the supplier's rate for its plant rather
  // than a flat dollar amount — the invoice generator resolves
  // hours × rate. Zero / absent means the adjustment is a flat dollar
  // amount (the CreditAmount / SupplementAmount above).
  const adjustmentHoursNum = Number(row.AdjustmentHours);
  return {
    ...row,
    rows,
    overtimeApprovals,
    plantNotes,
    AdjustmentType: adjustmentType,
    IsSupplement: isSupplement,
    IsCredit: isCredit,
    IsAdjustment: isAdjustment,
    OriginalTimesheetId: row.OriginalTimesheetId
      ? String(row.OriginalTimesheetId)
      : '',
    // Plant + supplier an adjustment targets. On legacy adjustments these
    // are empty and attribution falls back to the parent timesheet's
    // first row; standalone adjustments (created on the Adjustments tab)
    // carry them directly so the invoice generator can attribute the
    // dollar impact without a parent.
    AdjustmentSupplierCode: String(row.AdjustmentSupplierCode || '')
      .trim()
      .toUpperCase(),
    AdjustmentPlantCode: String(row.AdjustmentPlantCode || '')
      .trim()
      .toUpperCase(),
    AdjustmentReason: row.AdjustmentReason || row.SupplementReason || '',
    // Keep SupplementReason in the decoded output too so legacy UI
    // references (already shipped) continue to render the same text.
    // Both fields point at the same value on read.
    SupplementReason: row.AdjustmentReason || row.SupplementReason || '',
    BillingDisposition: row.BillingDisposition || '',
    // Exact day the adjustment is for (Oct 2026); WeekStart is the
    // Sunday of that week. Empty on adjustments made before the field.
    AdjustmentDate: String(row.AdjustmentDate || '').slice(0, 10),
    CreditAmount: Number.isFinite(creditAmountNum) ? creditAmountNum : 0,
    SupplementAmount: Number.isFinite(supplementAmountNum)
      ? supplementAmountNum
      : 0,
    AdjustmentHours:
      Number.isFinite(adjustmentHoursNum) && adjustmentHoursNum > 0
        ? adjustmentHoursNum
        : 0,
  };
}

/**
 * Serialize the UI-side model for SharePoint. The model shape the caller
 * passes is whatever makes sense to the form (`{ rows, status, ... }`);
 * this flattens it to column-named fields and re-runs the totals so we
 * never trust client-side rollups.
 */
function encode(model, { rate = MILEAGE_RATE_USD_PER_MILE } = {}) {
  const rows = Array.isArray(model.rows) ? model.rows : [];
  const totals = computeTotals(rows, rate);
  // `id` is the SharePoint system field — never include it in the fields
  // payload; surrounding helpers (updateItem) use it in the URL.
  const {
    id: _id,
    rows: _r,
    overtimeApprovals,
    plantNotes: pn,
    ...rest
  } = model;
  // OT approvals — JSON-serialize the deduped, upper-cased pair keys
  // back onto OvertimeApprovalsJson. Always written (even when empty)
  // so a previously-checked pair can be cleared by encoding through
  // saveDraft / approve / reject paths.
  const otList = Array.isArray(overtimeApprovals)
    ? Array.from(
        new Set(
          overtimeApprovals
            .map((k) => String(k || '').trim().toUpperCase())
            .filter(Boolean),
        ),
      )
    : [];
  // Serialize per-plant notes back to PlantNotesJson. Drop empty
  // strings so the column stays tidy. If the SharePoint column doesn't
  // exist yet, SharePoint simply ignores this field on write (the notes
  // won't persist until the column is added) — nothing else breaks.
  const notesOut = {};
  if (pn && typeof pn === 'object' && !Array.isArray(pn)) {
    for (const [k, v] of Object.entries(pn)) {
      const text = String(v ?? '').trim();
      if (k && text) notesOut[k] = text;
    }
  }
  return {
    ...rest,
    RowsJson: JSON.stringify(rows),
    OvertimeApprovalsJson: JSON.stringify(otList),
    PlantNotesJson: JSON.stringify(notesOut),
    TotalHours: totals.totalHours,
    TotalMiles: totals.totalMiles,
    MileageRateUsed: totals.mileageRateUsed,
    TotalMileageDollars: totals.totalMileageDollars,
  };
}

// ---- Reads -----------------------------------------------------------

export async function listTimesheets(opts) {
  const rows = await listItems(LIST, {
    orderby: 'fields/WeekStart desc',
    ...opts,
  });
  return rows.map(decode);
}

/**
 * Pull every timesheet for one submitter. SharePoint can't index a JSON
 * column, but SubmitterId is a plain text column — the caller fans this
 * out per-user, so full-list reads stay the fallback rather than the
 * default.
 */
export async function listTimesheetsForSubmitter(submitterId) {
  if (!submitterId) return [];
  const rows = await listItems(LIST, {
    orderby: 'fields/WeekStart desc',
  });
  const target = String(submitterId);
  return rows
    .filter((r) => String(r.SubmitterId || '') === target)
    .map(decode);
}

/**
 * Fetch a specific (submitter, week) timesheet. Returns null when the
 * pair hasn't been saved yet — the page then opens a fresh Draft in
 * memory without writing until the user actually enters something.
 */
/**
 * Load a single timesheet by its SharePoint list-item id. Used by the
 * Approvals tab's Edit flow, which routes to
 * /timesheets/submit?edit=<id>. Returns the same decoded shape every
 * other fetch returns (rows parsed out of RowsJson, etc.) so the
 * editor can swap straight in.
 */
export async function getTimesheetById(id) {
  if (!id) throw new Error('getTimesheetById: id required');
  return decode(await getItem(LIST, id));
}

export async function getTimesheet(submitterId, weekStart) {
  if (!submitterId || !weekStart) return null;
  const rows = await listItems(LIST, {});
  const target = String(submitterId);
  const week = String(weekStart);
  const hit = rows.find(
    (r) =>
      String(r.SubmitterId || '') === target && (r.WeekStart || '') === week,
  );
  return hit ? decode(hit) : null;
}

// ---- Writes ----------------------------------------------------------

/**
 * Save a Draft (or Rejected) timesheet. Creates the row the first time,
 * updates it on subsequent saves. Refuses to write a non-draft status
 * through this path — submit/approve/reject have their own helpers so
 * the allowed transitions stay auditable.
 */
export async function saveDraftTimesheet(model) {
  if (
    model.Status &&
    model.Status !== STATUS.DRAFT &&
    model.Status !== STATUS.REJECTED
  ) {
    throw new Error(
      `saveDraftTimesheet can't write status "${model.Status}" — use submit/approve/reject helpers`,
    );
  }
  const payload = encode({ ...model, Status: STATUS.DRAFT });
  if (model.id) {
    return decode(await updateItem(LIST, model.id, payload));
  }
  // Title is the implicit SharePoint column — populate it so the native
  // list view reads as "Jillian Lamb — 2026-04-20" instead of blank.
  if (!payload.Title) {
    payload.Title = `${model.SubmitterName || 'Timesheet'} — ${model.WeekStart || ''}`.trim();
  }
  return decode(await createItem(LIST, payload));
}

/**
 * Flip a Draft to Pending Approval. Stamps SubmittedAt. Does NOT let the
 * caller change the grid at the same time — they should saveDraft first,
 * then submit, so the submitted state always matches what they last saw.
 */
export async function submitTimesheetForApproval(id) {
  if (!id) throw new Error('submitTimesheetForApproval: id required');
  const raw = await updateItem(LIST, id, {
    Status: STATUS.PENDING,
    SubmittedAt: new Date().toISOString(),
    // Clear any prior rejection stamp so a resubmitted timesheet reads
    // cleanly. RejectionReason stays in place as history. IMPORTANT:
    // date columns reject empty strings via the Graph API — null (not
    // '') is the correct "no value" payload, and empty string returns
    // a 400 "Invalid request" that poisons the whole save.
    RejectedAt: null,
    RejectedBy: '',
  });
  return decode(raw);
}

export async function approveTimesheet(id, approver) {
  if (!id) throw new Error('approveTimesheet: id required');
  const raw = await updateItem(LIST, id, {
    Status: STATUS.APPROVED,
    ApprovedAt: new Date().toISOString(),
    ApprovedBy: approver || '',
  });
  return decode(raw);
}

export async function rejectTimesheet(id, rejector, reason) {
  if (!id) throw new Error('rejectTimesheet: id required');
  const raw = await updateItem(LIST, id, {
    Status: STATUS.REJECTED,
    RejectedAt: new Date().toISOString(),
    RejectedBy: rejector || '',
    RejectionReason: reason || '',
  });
  return decode(raw);
}

/**
 * RM patch on an already-Approved timesheet. Re-runs totals so a rate
 * edit or hours tweak propagates to TotalHours / TotalMiles. Status
 * stays Approved.
 */
export async function updateApprovedTimesheet(id, model) {
  if (!id) throw new Error('updateApprovedTimesheet: id required');
  const payload = encode({ ...model, Status: STATUS.APPROVED });
  return decode(await updateItem(LIST, id, payload));
}

/**
 * Create an adjustment (supplement or credit) timesheet linked to an
 * existing (typically Approved) timesheet. Used by the "Add Supplement"
 * and "Issue Credit" flows on the Approvals page.
 *
 * The adjustment is a real, standalone Timesheet row that:
 *   • carries the same submitter / WeekStart as the original
 *   • lands in Status='Pending' so it flows through the normal RM
 *     approval queue
 *   • holds AdjustmentType + OriginalTimesheetId so the UI and the
 *     Billing roll-up can distinguish it
 *   • requires an AdjustmentReason and a BillingDisposition appropriate
 *     to the type (supplement → supplemental-invoice / next-invoice;
 *     credit → credit-memo / next-invoice-credit)
 *   • for credits, additionally carries CreditAmount (flat dollar
 *     amount being credited back to the customer)
 *
 * @param {object} model — adjustment payload. Required fields:
 *   AdjustmentType, OriginalTimesheetId, AdjustmentReason,
 *   BillingDisposition. Credits additionally require CreditAmount > 0.
 *   The rest mirrors a regular timesheet model.
 */
export async function createAdjustmentTimesheet(model) {
  // An adjustment must be attributable to a supplier. The legacy flow
  // links it to a parent timesheet (OriginalTimesheetId) and derives the
  // supplier from that parent's rows. The standalone flow (Adjustments
  // tab) instead names the plant + supplier directly. Require one or the
  // other.
  const hasParent = !!model?.OriginalTimesheetId;
  const hasPair =
    !!String(model?.AdjustmentSupplierCode || '').trim() &&
    !!String(model?.AdjustmentPlantCode || '').trim();
  if (!hasParent && !hasPair) {
    throw new Error(
      'createAdjustmentTimesheet: provide OriginalTimesheetId, or AdjustmentSupplierCode + AdjustmentPlantCode',
    );
  }
  const type = String(model?.AdjustmentType || '').toLowerCase();
  if (!type || !Object.values(ADJUSTMENT_TYPES).includes(type)) {
    throw new Error(
      `createAdjustmentTimesheet: AdjustmentType must be one of ${Object.values(ADJUSTMENT_TYPES).join(', ')}`,
    );
  }
  if (!model?.AdjustmentReason) {
    throw new Error(
      'createAdjustmentTimesheet: AdjustmentReason required (one-line note)',
    );
  }
  if (!model?.BillingDisposition) {
    throw new Error(
      'createAdjustmentTimesheet: BillingDisposition required',
    );
  }
  const validDispositions =
    type === ADJUSTMENT_TYPES.CREDIT
      ? CREDIT_DISPOSITIONS
      : SUPPLEMENT_DISPOSITIONS;
  if (!validDispositions.includes(model.BillingDisposition)) {
    throw new Error(
      `createAdjustmentTimesheet: BillingDisposition for a ${type} must be one of ${validDispositions.join(', ')}`,
    );
  }
  // Credits AND supplements each need a positive VALUE — but that value
  // can be either a flat dollar amount OR a number of hours (per
  // Jillian, July 2026). Hours-based adjustments are priced on the
  // invoice at the supplier's rate for the plant; dollar-based ones
  // carry the flat figure. Exactly one of the two must be positive.
  let creditAmount = 0;
  let supplementAmount = 0;
  const adjustmentHours = Number(model.AdjustmentHours);
  const hasHours = Number.isFinite(adjustmentHours) && adjustmentHours > 0;
  if (type === ADJUSTMENT_TYPES.CREDIT) {
    creditAmount = Number(model.CreditAmount);
    const hasDollars = Number.isFinite(creditAmount) && creditAmount > 0;
    if (!hasHours && !hasDollars) {
      throw new Error(
        'createAdjustmentTimesheet: enter a positive number of hours or a positive CreditAmount',
      );
    }
  }
  if (type === ADJUSTMENT_TYPES.SUPPLEMENT) {
    supplementAmount = Number(model.SupplementAmount);
    const hasDollars = Number.isFinite(supplementAmount) && supplementAmount > 0;
    if (!hasHours && !hasDollars) {
      throw new Error(
        'createAdjustmentTimesheet: enter a positive number of hours or a positive SupplementAmount',
      );
    }
  }
  const payload = encode({
    ...model,
    AdjustmentType: type,
    // Maintain the legacy IsSupplement column on writes for supplement
    // rows so older readers that haven't been migrated still recognize
    // them. Drop the column on credit writes (it would be misleading).
    IsSupplement: type === ADJUSTMENT_TYPES.SUPPLEMENT,
    // Normalised pair (empty on legacy parent-linked adjustments).
    AdjustmentSupplierCode: String(model.AdjustmentSupplierCode || '')
      .trim()
      .toUpperCase(),
    AdjustmentPlantCode: String(model.AdjustmentPlantCode || '')
      .trim()
      .toUpperCase(),
    AdjustmentReason: String(model.AdjustmentReason).trim(),
    // SharePoint Date columns reject '' — send undefined when blank.
    AdjustmentDate: model.AdjustmentDate || undefined,
    // Don't write SupplementReason — AdjustmentReason supersedes it.
    SupplementReason: undefined,
    // Dollar amount is written only in dollar mode; in hours mode it's
    // left off (null via undefined) so the invoice reads the hours path.
    CreditAmount:
      type === ADJUSTMENT_TYPES.CREDIT && !hasHours ? creditAmount : undefined,
    SupplementAmount:
      type === ADJUSTMENT_TYPES.SUPPLEMENT && !hasHours
        ? supplementAmount
        : undefined,
    // Hours drive the invoice's rate lookup when present; omitted in
    // dollar mode. SharePoint Number columns reject '' — send undefined.
    AdjustmentHours: hasHours ? adjustmentHours : undefined,
    Status: STATUS.PENDING,
    SubmittedAt: new Date().toISOString(),
  });
  return decode(await createItem(LIST, payload));
}

/**
 * Backward-compatible alias for createAdjustmentTimesheet that pins the
 * type to 'supplement'. Kept for any callers still on the old name —
 * new callers should use createAdjustmentTimesheet directly.
 */
export async function createSupplementTimesheet(model) {
  return createAdjustmentTimesheet({
    ...model,
    AdjustmentType: ADJUSTMENT_TYPES.SUPPLEMENT,
    AdjustmentReason: model?.AdjustmentReason || model?.SupplementReason || '',
  });
}

/**
 * Return the list of adjustments (supplements + credits) written
 * against a given timesheet. Used by the Approvals page to surface
 * the chain and by the Billing dashboard to enumerate the period's
 * adjustments. Filters client-side because the OriginalTimesheetId
 * column isn't indexed.
 */
export async function listAdjustmentsFor(originalTimesheetId) {
  if (!originalTimesheetId) return [];
  const target = String(originalTimesheetId);
  const all = await listTimesheets();
  return all.filter(
    (t) => t.IsAdjustment && String(t.OriginalTimesheetId) === target,
  );
}

// Legacy alias — returns only supplements (filters credits out) so
// existing callers expecting "supplements" don't suddenly see credits
// mixed in. New callers should use listAdjustmentsFor.
export async function listSupplementsFor(originalTimesheetId) {
  const all = await listAdjustmentsFor(originalTimesheetId);
  return all.filter((t) => t.IsSupplement);
}

/**
 * RM patch on a Pending timesheet — used by the Approvals page's "Edit"
 * flow. The RM can correct hours, plant/supplier rows, mileage, or
 * expenses on a submitted timesheet without sending it back to the
 * submitter as Rejected. Status stays Pending so the same RM (or a
 * Director) can review the corrected version and approve in a separate
 * click. For the "Save & Approve" one-shot, the caller chains this
 * with approveTimesheet(id, approver).
 *
 * Symmetric to updateApprovedTimesheet — we re-run encode so the
 * computed totals (TotalHours, TotalMiles, etc.) stay in sync with
 * the edited rows.
 */
export async function updatePendingTimesheet(id, model, opts = {}) {
  if (!id) throw new Error('updatePendingTimesheet: id required');
  const payload = encode({ ...model, Status: STATUS.PENDING });
  // A Director retracting an Approved tracker back to Pending (Nov 2026)
  // clears the old approval stamp. Date columns need null, not ''.
  if (opts.clearApproval) {
    payload.ApprovedAt = null;
    payload.ApprovedBy = '';
  }
  return decode(await updateItem(LIST, id, payload));
}

/**
 * Toggle (or set) a single (plant, supplier) pair's OT-billing
 * approval on a timesheet. Used by the RM Approvals checkbox so a
 * click immediately persists without changing status or going
 * through the full encode path.
 *
 * @param {string|number} id          — SharePoint listItem id
 * @param {string[]}      currentList — current decoded overtimeApprovals
 *                                       array (so we don't refetch)
 * @param {string}        pairKey     — "PLANTCODE|SUPPLIERCODE"
 * @param {boolean}       approved    — desired state
 * @returns {Promise<string[]>} the new approvals list
 */
export async function setTimesheetOvertimeApproval(
  id,
  currentList,
  pairKey,
  approved,
) {
  if (!id) throw new Error('setTimesheetOvertimeApproval: id required');
  const key = String(pairKey || '').trim().toUpperCase();
  if (!key) return Array.isArray(currentList) ? currentList.slice() : [];
  const set = new Set(
    Array.isArray(currentList)
      ? currentList.map((k) => String(k || '').trim().toUpperCase())
      : [],
  );
  if (approved) set.add(key);
  else set.delete(key);
  const next = Array.from(set);
  // Patch the JSON column directly — no re-encode of rows / totals,
  // so this stays cheap and won't trigger a recompute round-trip on
  // every checkbox click.
  await updateItem(LIST, id, {
    OvertimeApprovalsJson: JSON.stringify(next),
  });
  return next;
}

/**
 * Fetch every timesheet in a given status. Used by the RM approval
 * inbox (status=Pending Approval) and — eventually — by the Billing
 * dashboard (status=Approved).
 */
export async function listTimesheetsByStatus(status, opts) {
  const rows = await listItems(LIST, {
    orderby: 'fields/WeekStart desc',
    ...opts,
  });
  return rows
    .filter((r) => (r.Status || '') === status)
    .map(decode);
}

// ---- Single-source fetch of the current Monday -----------------------
// Kept alongside the rest of the week math so every caller asking
// "what week is it right now?" gets the same answer.
export function currentWeekStart() {
  return weekStartFromDate(new Date());
}

// First week the Billing Tracker is open for entries — the SUNDAY that
// starts the go-live week (Sunday, August 2, 2026; the week runs Aug 2–8),
// per Jillian. The submit page won't navigate to any week before this, so
// no entries can be logged against earlier weeks — nobody can "go back in
// time" and enter hours for a past week. This is a fixed go-live anchor,
// NOT the rolling current week: it must stay put so this week's entries
// remain editable next week and beyond.
export const BILLING_TRACKER_FIRST_WEEK = '2026-08-02';

/**
 * The week the Billing Tracker submit page lands on: the current week,
 * but never earlier than BILLING_TRACKER_FIRST_WEEK. Now that the first
 * week is the current week, this simply lands on the current week.
 */
export function defaultWeekStart() {
  const cur = currentWeekStart();
  return cur < BILLING_TRACKER_FIRST_WEEK ? BILLING_TRACKER_FIRST_WEEK : cur;
}

/**
 * Delete a timesheet by id. Used by the pre-launch cleanup tool on
 * MyAccount to wipe a director's own test timesheets ahead of go-live.
 * Receipts attached to the timesheet (in the TimesheetReceipts library)
 * are NOT cascaded — they reference the timesheet by id, so they
 * become orphans here. That's fine for the cleanup case (we're
 * tossing everything), but worth knowing if this gets reused for
 * one-off deletions later.
 */
export async function deleteTimesheet(id) {
  return deleteItem(LIST, id);
}
